import axios, { AxiosRequestConfig, AxiosResponse, AxiosRequestHeaders } from 'axios';
import {
  ApiResponse,
  PayloadConstructor,
  EdgeProposition,
  EdgePropositionTokens,
} from './serverTarget.types';
import { defaultUrl, defaultHeaders } from './constant';
import {
  buildKndctrStateEntries,
  decodeEcidFromKndctrIdentityCookie,
  findCookieCaseInsensitive,
  getKndctrCookieKey,
} from './adobeUtils';
import { logger } from '../logger';

// Server-side logger for AEP Edge interact diagnostics inside getAdobeAEPData.
const { log } = logger({})('[getAdobeAEPData]');


export const getTargetExpSegment = async (
  url: string = defaultUrl,
  headers: AxiosRequestHeaders = {} as AxiosRequestHeaders,
  createReqObj: PayloadConstructor | object | null = null, // allow function or object or null
  queryParams: object = {}
): Promise<ApiResponse> => {
  try {
    const defaultPayload = createDefaultPayload();
    let payload = defaultPayload;
    if (createReqObj) {
      if (typeof createReqObj === 'function') {
        payload = createReqObj(defaultPayload);
      } else if (typeof createReqObj === 'object') {
        payload = createReqObj;
      }
    }

    const mergedHeaders: AxiosRequestHeaders = {
      ...defaultHeaders,
      ...headers,
    } as AxiosRequestHeaders;

    const config: AxiosRequestConfig = {
      headers: mergedHeaders,
      params: queryParams,
    };
    const response: AxiosResponse = await axios.post(url, payload, config);
    return { data: response.data };
  } catch (error) {
    return { data: null };
  }
};

/*
  Please do not make any changes to this structure.If you need any changes to the structure you can do it inside the application from which this utility is getting called.
*/
export const createDefaultPayload = (): object => {
  return {
    context: {
      userAgent: 'test',
      timeOffsetInMinutes: 0,
      channel: 'web',
      browser: {
        host: process.env['NEXT_PUBLIC_TARGET_BASE_URL'],
        webGLRenderer: null,
      },

      address: {
        url: '',
        referringUrl: null,
      },
    },

    id: {
      tntId: process.env['NEXT_PUBLIC_TNT_ID'],
    },

    property: {
      token: process.env['NEXT_PUBLIC_PROPERTY_TOKEN'],
    },

    experienceCloud: {
      analytics: {
        supplementalDataId: process.env['NEXT_PUBLIC_SUPPLEMENTAL_DATA_ID'],
        trackingServer: 'metrics.marriott.com',
        trackingServerSecure: 'smetrics.marriott.com',
        logging: 'server_side',
      },
    },

    execute: {
      mboxes: null,
    },
  };
};

const buildCurlCommand = (
  url: string,
  method: string,
  headers: Record<string, string>,
  body: string
): string => {
  const escapeForSingleQuotedShell = (value: string): string =>
    value.replace(/'/g, `'\\''`);

  const headerFlags = Object.entries(headers)
    .map(([key, value]) => `-H '${escapeForSingleQuotedShell(`${key}: ${value}`)}'`)
    .join(' \\\n  ');

  return `curl -X ${method} '${escapeForSingleQuotedShell(url)}' \\\n  ${headerFlags} \\\n  -d '${escapeForSingleQuotedShell(body)}'`;
};

export const getAdobeTargetData = async (pageURL: string, decisionScopes: string[], targetParameters: Record<string, unknown> = {}, browserCookies: Record<string, string> = {}) => {
  const messages: Record<string, unknown> = {}, eventInfo : { messages: Record<string, unknown> } = {
    messages: {
      adobe_interact_response: {}
    }
  };

  const AEP_EDGE_API_KEY = process.env['NEXT_PUBLIC_AEP_EDGE_API_KEY'];
  const AEP_EDGE_DATASTREAM_ID = process.env['NEXT_PUBLIC_AEP_DATASTREAM_ID'];
  const AEP_EDGE_ORG_ID = process.env['NEXT_PUBLIC_TARGET_ORG_ID'];
  const AEP_EDGE_SANDBOX_NAME = process.env['NEXT_PUBLIC_AEP_SANDBOX_NAME'];
  const EDGE_DOMAIN = process.env['NEXT_PUBLIC_TARGET_DOMAIN'];

  if (!decisionScopes?.length) {
    log.debug('AEP Edge interact call skipped: missing decisionScopes');
    messages['error'] = 'Missing decisionScopes';
    return { data: null, messages };
  }

  const adobeEntries = buildKndctrStateEntries(browserCookies);
  const cluster = findCookieCaseInsensitive(browserCookies, getKndctrCookieKey('cluster'));
  const identityValue = findCookieCaseInsensitive(browserCookies, getKndctrCookieKey('identity'));
  const ecid = identityValue ? decodeEcidFromKndctrIdentityCookie(identityValue) : null;

  messages['adobe_cookiesAvailability'] = {
    identity: {
      key: getKndctrCookieKey('identity'),
      present: Boolean(identityValue),
      value: identityValue ?? null,
    },
    cluster: {
      key: getKndctrCookieKey('cluster'),
      present: Boolean(cluster),
      value: cluster ?? null,
    },
  };

  if (!(Boolean(identityValue) && Boolean(cluster))) {
    // if we dont have identity and cluster, then we don't need to make the call to AEP Edge
    messages['adobe_interact_error'] = 'No identity and cluster cookies found, skipping AEP Edge call';
    return {
      data: null,
      messages,
    };
  }

  const payload = {
    event: {
      xdm: {
        eventType: 'decisioning.propositionFetch',
        timestamp: new Date().toISOString(),
        web: {
          webPageDetails: { URL: pageURL },
        },
        ...(ecid
          ? {
            identityMap: {
              ECID: [{ id: ecid, primary: true }],
            },
          }
          : {}),
      },
      data: {
        __adobe: {
          target: targetParameters,
        },
      },
    },
    query: {
      personalization: {
        schemas: [
          "https://ns.adobe.com/personalization/default-content-item",
          "https://ns.adobe.com/personalization/html-content-item",
          "https://ns.adobe.com/personalization/json-content-item",
          "https://ns.adobe.com/personalization/redirect-item",
          "https://ns.adobe.com/personalization/dom-action"
        ],
        decisionScopes,
      },
    },
    meta: {
      configOverrides: {
        com_adobe_target: {
          propertyToken: process.env['NEXT_PUBLIC_PROPERTY_TOKEN'],
        }
      },
      state: adobeEntries.length
        ? {
          entries: adobeEntries,
          domain: EDGE_DOMAIN,
          cookiesEnabled: true,
        }
        : {
          domain: EDGE_DOMAIN,
          cookiesEnabled: true,
        },
    },
  };

  /**
   * Build the AEP Edge interact URL
   */
  const AEP_EDGE_INTERACT_URL = (() => {
    const AEP_EDGE_URL = process.env['NEXT_PUBLIC_AEP_EDGE_URL'];
    const requestId = String(Date.now());
    const base = cluster ? `${AEP_EDGE_URL}/ee/${cluster}/v2/interact` : `${AEP_EDGE_URL}/ee/v2/interact`;
    return `${base}?datastreamId=${AEP_EDGE_DATASTREAM_ID}&requestId=${requestId}`;
  })();

  const requestHeaders = Object.fromEntries(
    Object.entries({
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'x-api-key': AEP_EDGE_API_KEY,
      'x-gw-ims-org-id': AEP_EDGE_ORG_ID,
      'x-sandbox-name': AEP_EDGE_SANDBOX_NAME,
    }).filter((entry): entry is [string, string] => entry[1] !== undefined)
  ) as Record<string, string>;
  const requestBody = JSON.stringify(payload);

  messages['adobe_interact_curl'] = buildCurlCommand(
    AEP_EDGE_INTERACT_URL,
    'POST',
    requestHeaders,
    requestBody
  );

  try {
    const response = await fetch(AEP_EDGE_INTERACT_URL, {
      method: 'POST',
      headers: requestHeaders,
      body: requestBody,
    });

    const adobeData = await response.json();
    messages['adobe_interact_response'] = adobeData;
    eventInfo['messages']['adobe_interact_response'] = {
      requestId: adobeData?.requestId,
      handle: Array.isArray(adobeData?.handle) ? [adobeData?.handle?.find((item: Record<string, unknown>) => item?.['type'] === 'personalization:decisions')] : []
    };

    if (!response.ok) {
      const adobeError = adobeData?.errors?.[0];
      messages['adobe_interact_error'] =
        adobeError?.detail ?? adobeError?.title ?? `Adobe Edge interact failed with status ${response.status}`;
      log.error(`AEP call failed: ${messages['adobe_interact_error']}`);
      if (adobeError?.report) {
        messages['adobe_interact_error_report'] = JSON.stringify(adobeError.report);
      }
      return { data: null, messages };
    }

    return {
      data: {
        propositions: parseEdgeInteractPropositions(adobeData, decisionScopes),
        cookies: extractKndctrCookiesFromHandle(adobeData),
      },
      messages,
      eventInfo,
    };
  } catch (error) {
    messages['error'] = JSON.stringify(error);
    return { data: null, messages, eventInfo };
  }

}

/** @deprecated Use {@link getAdobeTargetData} instead. Kept for backward compatibility. */
export const getAdobeAEPData = getAdobeTargetData;

const normalizePropositionContent = (raw: unknown): string | undefined => {
  if (raw === null || raw === undefined) {
    return undefined;
  }
  if (typeof raw === 'string') {
    return raw;
  }
  if (typeof raw === 'boolean' || typeof raw === 'number') {
    return String(raw);
  }
  if (typeof raw === 'object') {
    const content = (raw as Record<string, unknown>)['content'];
    if (typeof content === 'string') {
      return content;
    }
    if (typeof content === 'boolean' || typeof content === 'number') {
      return String(content);
    }
  }
  return undefined;
};

const buildPropositionTokens = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  proposition: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  firstItem: any
): EdgePropositionTokens => {
  const activityName =
    proposition?.scopeDetails?.activity?.name ??
    proposition?.scopeDetails?.experience?.name ??
    firstItem?.meta?.['experience.name'];
  const optionName =
    firstItem?.meta?.['experience.name'] ??
    proposition?.scopeDetails?.experience?.name ??
    proposition?.scopeDetails?.correlationID;

  const tokens: EdgePropositionTokens = {};
  if (activityName) {
    tokens['activity.name'] = activityName;
  }
  if (optionName) {
    tokens['option.name'] = optionName;
  }
  return tokens;
};

/** Maps Edge `personalization:decisions` handles to normalized proposition rows. */
export const parseEdgeInteractPropositions = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  adobeData: any,
  requestedScopes: string[] = []
): EdgeProposition[] => {
  const decisionsHandle = adobeData?.handle?.find(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (handle: any) => handle?.type === 'personalization:decisions'
  );
  const propositionsByScope = new Map<string, EdgeProposition>();

  decisionsHandle?.payload?.forEach(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (proposition: any) => {
      const scope = proposition?.scope;
      if (!scope) {
        return;
      }

      const firstItem = proposition?.items?.[0];
      const content = normalizePropositionContent(firstItem?.data?.content);
      if (!content) {
        return;
      }

      propositionsByScope.set(scope, {
        scope,
        content,
        tokens: buildPropositionTokens(proposition, firstItem),
      });
    }
  );

  if (!requestedScopes.length) {
    return Array.from(propositionsByScope.values());
  }

  return requestedScopes.map(scope => {
    return (
      propositionsByScope.get(scope) ?? {
        scope,
        content: '',
        tokens: {},
      }
    );
  });
};

const extractKndctrCookiesFromHandle = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  adobeData: any
): Record<string, string> => {
  const stateHandle = adobeData?.handle?.find(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (handle: any) => handle?.type === 'state:store'
  );
  const cookies: Record<string, string> = {};

  stateHandle?.payload?.forEach(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (entry: any) => {
      if (entry?.key?.startsWith('kndctr_') && entry?.value) {
        cookies[entry.key] = entry.value;
      }
    }
  );

  return cookies;
};
